import { AdminTable } from './shared/AdminDataGrid';
/**
 * Cron Jobs Monitoring Component
 * Real-time monitoring and manual triggering of scheduled cron jobs
 */

import { useState, useEffect } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import { apiClient } from '../providers/apiClient';
import { logger } from '../utils/logger';
import { semantic } from '@rekindle/shared/theme';

// ── Types ──────────────────────────────────────────────────────────────────────

type CronJobStatus = 'running' | 'success' | 'failure';

interface CronJobLog {
  status: CronJobStatus;
  startedAt: string;
  durationMs: number | null;
  errorMessage: string | null;
}

interface CronJob {
  name: string;
  schedule: string;
  enabled: boolean;
  running: boolean;
  lastRun: CronJobLog | null;
  lastCheck?: { at: string; result: 'idle' | 'due' | 'error' } | null;
}

interface GetCronJobsResponse {
  jobs: CronJob[];
}

// ── Utilities ──────────────────────────────────────────────────────────────────

/**
 * Get MUI color for job status
 */
function getStatusColor(status: CronJobStatus | null): 'success' | 'error' | 'warning' | 'default' {
  if (!status) return 'default';
  if (status === 'success') return 'success';
  if (status === 'failure') return 'error';
  if (status === 'running') return 'warning';
  return 'default';
}

/**
 * Get status label with emoji
 */
function getStatusLabel(status: CronJobStatus | null): string {
  if (!status) return 'Never Run';
  if (status === 'success') return '✅ Success';
  if (status === 'failure') return '❌ Failed';
  if (status === 'running') return '🔄 Running';
  return status;
}

/**
 * Format duration in milliseconds as human-readable
 */
function formatDuration(durationMs: number | null): string {
  if (durationMs === null) return '—';
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(1)}s`;
}

/**
 * Format date as relative time
 */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch (error) {
    logger.error('Failed to format date', { dateString, error });
    return dateString;
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export function CronJobs() {
  const notify = useNotify();
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningJobs, setRunningJobs] = useState<Set<string>>(new Set());

  /**
   * Fetch cron jobs from API
   */
  const fetchJobs = async () => {
    try {
      const response = await apiClient.get<GetCronJobsResponse>('/admin/cron/jobs');
      setJobs(response.jobs);
      logger.debug('Cron jobs loaded', { count: response.jobs.length });
    } catch (error) {
      logger.error('Failed to load cron jobs', error);
      notify('Failed to load cron jobs', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Manually trigger a cron job
   */
  const handleRunJob = async (jobName: string) => {
    setRunningJobs((prev) => new Set(prev).add(jobName));
    logger.info('Triggering cron job', { jobName });

    try {
      await apiClient.post(`/admin/cron/jobs/${jobName}/run`);
      notify(`Cron job "${jobName}" triggered successfully`, { type: 'success' });
      logger.info('Cron job triggered successfully', { jobName });

      // Refresh after 2 seconds to show new log entry
      setTimeout(fetchJobs, 2000);
    } catch (error: unknown) {
      const errorResponse = error as { response?: { status?: number } };
      if (errorResponse.response?.status === 409) {
        notify('Job is already running', { type: 'warning' });
        logger.warn('Job already running', { jobName });
      } else {
        notify('Failed to trigger job', { type: 'error' });
        logger.error('Failed to trigger job', { jobName, error });
      }
    } finally {
      setRunningJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobName);
        return next;
      });
    }
  };

  // Initial load
  useEffect(() => {
    fetchJobs();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Title title="Cron Jobs" />
      <Card>
        <CardContent>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="h2">
              Cron Job Monitoring
            </Typography>
            <Button variant="outlined" size="small" onClick={fetchJobs}>
              Refresh
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <AdminTable>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Job Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Schedule</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Last Execution</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Duration</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Error</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No cron jobs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job) => (
                    <TableRow key={job.name} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component="code"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {job.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component="code"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {job.schedule}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatRelativeTime(job.lastRun?.startedAt || null)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last checked on this instance: {formatRelativeTime(job.lastCheck?.at || null)}
                          {job.lastCheck ? ` (${job.lastCheck.result})` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusLabel(job.lastRun?.status || null)}
                            color={getStatusColor(job.lastRun?.status || null)}
                            size="small"
                          />
                          {job.running && <CircularProgress size={16} />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDuration(job.lastRun?.durationMs || null)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {job.lastRun?.errorMessage ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: semantic.error,
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={job.lastRun.errorMessage}
                          >
                            {job.lastRun.errorMessage}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleRunJob(job.name)}
                          disabled={job.running || runningJobs.has(job.name) || !job.enabled}
                          sx={{ minWidth: 100 }}
                        >
                          {runningJobs.has(job.name) ? (
                            <>
                              <CircularProgress size={14} sx={{ mr: 1 }} />
                              Running...
                            </>
                          ) : (
                            'Run Now'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </AdminTable>
          </TableContainer>

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Auto-refreshes every 30 seconds • Showing {jobs.length} cron job
              {jobs.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
